import { LomavisH2 } from '@/lomavisSharedUi/atoms/typography/LomavisH2';
import { LomavisGrowthCardSectionTemplate } from '@/lomavisSharedUi/templates/cards/LomavisGrowthCardSectionTemplate';
import { fetchPerfomanceData } from '@/redux/slices/analytics/postPerformance';
import { lomavisShowToastr } from '@/utils/ui/toastr';
import { AnyAction, ThunkDispatch } from '@reduxjs/toolkit';
import { useTranslate } from '@tolgee/react';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  allContentTypePerformanceConfig,
  allPlatformsPostPerformanceConfig,
  filterExistingPlatforms,
  filterPostsByPlatform,
  getAveragePostPerformanceMetrics,
  getContentTypeMetrics,
  getDateContentMetrics,
  getPerformanceSumMetrics,
  getPlatformContentMetrics,
  getPlatformMetrics,
  getPlatformPerformanceMetrics,
  getPostPerformanceMetrics,
  METRIC_NAMES
} from './helpers';
import { LomavisCard1 } from '@/lomavisSharedUi/atoms/cards/LomavisCard1';
import { LomavisDoughnutChart1 } from '@/lomavisSharedUi/atoms/charts/LomavisDoughnutChart1';
import { PLATFORM_LIST } from '@/config/constants';
import { LomavisBarChart1 } from '@/lomavisSharedUi/atoms/charts/LomavisBarChart1';
import { LomavisBestPerformingPosts } from '@/lomavisSharedUi/templates/cards/ILomavisBestPerformingPosts';
import {
  getDefaultRanges,
  LomavisDateRangeInput1
} from '@/lomavisSharedUi/atoms/inputs/LomavisRangeCalendar1';
import { useLocales } from '@/locales';
import IsLoadingIndicator from '@/components/isLoadingIndicator';
import { LomavisBarChart2 } from '@/lomavisSharedUi/atoms/charts/LomavisBarChart2';
import { ILomavisPlatformContentType1 } from '@/lomavisSharedUi/molecules/charts/ILomavisPlatformContentType1';
import { Spinner } from 'react-bootstrap';
import { LomavisShortenedNumber } from '@/lomavisSharedUi/atoms/utils/numbers/LomavisShortenedNumber';
import { LomavisBarChart3 } from '@/lomavisSharedUi/atoms/charts/LomavisBarChart3';
import { LomavisH3 } from '@/lomavisSharedUi/atoms/typography/LomavisH3';
import { formatDateAsISODateString } from '@/utils/dateUtils';

interface Props {
  platform?: string;
}

const PostPerformance: React.FC<Props> = ({ platform }) => {
  const { t } = useTranslate();
  const dispatch = useDispatch<ThunkDispatch<any, any, AnyAction>>();
  const { locationslug } = useParams();

  const { loadingData } = useSelector((state: any) => state.postPerformance);
  const data = useSelector(
    (state: any) => state.postPerformance.postPerformanceData
  );
  const { currentLang } = useLocales();
  const defaultRanges = getDefaultRanges();
  const calendar_default_index = 2;
  const [range, setRange] = useState<{ startDate?: Date; endDate?: Date }>(
    defaultRanges[calendar_default_index]
  );

  const [postPerformanceData, setPostPerformanceData] = useState([]);

  useEffect(() => {
    if (platform) {
      const filteredData = filterPostsByPlatform(data, platform);
      console.log('filtered posts', filteredData);
      setPostPerformanceData(filteredData);
    } else {
      setPostPerformanceData(data);
    }
  }, [platform, data]);

  useEffect(() => {
    if (locationslug) {
      dispatch(
        fetchPerfomanceData({
          locationSlug: locationslug,
          onError: (err) => {
            lomavisShowToastr(t('platform_analytics.err_gettingPost'), 'error');
            console.error('Error loading post data', err);
          },
          date_after: range?.startDate && formatDateAsISODateString(range?.startDate),
          date_before: range?.endDate && formatDateAsISODateString(range?.endDate)
        })
      );
    }
  }, [locationslug, range]);
  const navBarHeight =
    document.getElementById('kt_app_header')?.clientHeight + 15 * 2 + 50;
  return (
    <div className="w-100">
      <div className="w-100">
        <div className="d-flex w-100 justify-content-between ">
          <div className="w-auto">
            {postPerformanceData?.length !== 0 && loadingData && (
              <div className="d-flex justify-content-center align-items-center w-100">
                <IsLoadingIndicator />
              </div>
            )}
          </div>
          <LomavisDateRangeInput1
            currentLang={currentLang}
            demo={false}
            setDates={(value: { startDate: Date; endDate: Date }) => {
              setRange(value);
            }}
            defaultIndex={calendar_default_index}
          />
        </div>

        <div className="m-5" />

        {postPerformanceData?.length !== 0 && (
          <div className="w-100 d-flex flex-column gap-3 ">
            <div className={'d-flex flex-column gap-3'}>
              <LomavisH2
                title={t(
                  'platform_analytics.post_performance_title.title.KpiSection'
                )}
              />
              <LomavisGrowthCardSectionTemplate
                title={t('platform_analytics.post_performance_title')}
                entries={getPostPerformanceMetrics(
                  postPerformanceData || [],
                  postPerformanceData || [],
                  allPlatformsPostPerformanceConfig,
                  t
                )}
              />
              <LomavisGrowthCardSectionTemplate
                title={t('platform_analytics.post_average_performance_title')}
                entries={getAveragePostPerformanceMetrics(
                  postPerformanceData || [],
                  postPerformanceData || [],
                  allPlatformsPostPerformanceConfig,
                  t,
                  Math.ceil(
                    (range.endDate.getTime() - range.startDate.getTime()) /
                      (1000 * 60 * 60 * 24)
                  ) + 1
                )}
              />
            </div>

            <div className={'d-flex flex-column'}>
              <div className="m-5" />
              <LomavisH2
                title={t(
                  'platform_analytics.post_performance_title.title.postsSection'
                )}
              />
              <div className="">
                <LomavisBestPerformingPosts
                  title={t('platform_analytics.performance_by_metric.title')}
                  itemsToShow={3}
                  data={postPerformanceData || []}
                  sortOptions={allPlatformsPostPerformanceConfig
                    ?.filter((item) => item?.metricName !== 'postCount')
                    .map((item) => ({
                      name: item?.metricName,
                      title: t(`${item?.title}`)
                    }))}
                />
              </div>
            </div>

            <div className="m-5" />
            <div className={'d-flex flex-column'}>
              <LomavisH2
                title={t(
                  'platform_analytics.post_performance_title.title.distributionSection'
                )}
              />
              <div className="row g-3">
                <div className="col-12 col-md-6">
                  <LomavisCard1
                    className="h-300px w-100"
                    title={
                      'platform_analytics.distribution_by_contentType_title'
                    }
                  >
                    <LomavisDoughnutChart1
                      data={getContentTypeMetrics(
                        postPerformanceData || [],
                        allContentTypePerformanceConfig,
                        t
                      )}
                    />
                  </LomavisCard1>
                </div>
                <div className="col-12 col-md-6">
                  <LomavisCard1
                    className="h-300px w-100"
                    title={'platform_analytics.distribution_by_platform_title'}
                  >
                    <LomavisDoughnutChart1
                      data={getPlatformMetrics(
                        postPerformanceData || [],
                        PLATFORM_LIST,
                        t
                      )}
                    />
                  </LomavisCard1>
                </div>
              </div>
            </div>

            <div className={'d-flex flex-column gap-3'}>
              <div className="m-5" />
              <LomavisH2
                title={t(
                  'platform_analytics.post_performance_title.postPerformanceSection'
                )}
              />
              <div className="w-100 d-flex flex-column gap-3">
                {allPlatformsPostPerformanceConfig?.map((metric) => (
                  <div className="w-100 h-300px">
                    <LomavisCard1
                      className="h-100 w-100"
                      title={`${metric?.title}`}
                    >
                      <div className="d-flex align-items-center w-100 h-100">
                        <span
                          style={{ fontSize: '64px' }}
                          className="w-25 fw-bolder d-flex justify-content-center align-items-center"
                        >
                          <LomavisShortenedNumber
                            value={getPerformanceSumMetrics(
                              postPerformanceData || [],
                              filterExistingPlatforms(
                                PLATFORM_LIST,
                                postPerformanceData
                              ) || [],
                              metric?.metricName,
                              t
                            )}
                          />
                          {metric?.metricName === METRIC_NAMES.ENGAGEMENT && (
                            <span>%</span>
                          )}
                        </span>
                        <span className="w-100 h-100">
                          <LomavisBarChart1
                            title=""
                            allowZero={true}
                            data={getPlatformPerformanceMetrics(
                              postPerformanceData || [],
                              filterExistingPlatforms(
                                PLATFORM_LIST,
                                postPerformanceData
                              ) || [],
                              metric?.metricName,
                              t
                            )}
                          />
                        </span>
                      </div>
                    </LomavisCard1>
                  </div>
                ))}
              </div>
            </div>

            <div className={'d-flex flex-column gap-3'}>
              <div className="m-5" />
              <LomavisH2
                title={t(
                  'platform_analytics.post_performance_title.postDevelopment'
                )}
              />
              <div className="w-100 h-800px">
                <LomavisCard1
                  className="h-100 w-100"
                  title={'platform_analytics.content_type_chart.title'}
                >
                  <LomavisBarChart3
                    data={getDateContentMetrics(
                      postPerformanceData || [],
                      allContentTypePerformanceConfig
                    )}
                  />
                </LomavisCard1>
              </div>
            </div>

            <div className={'d-flex flex-column gap-3'}>
              <div className="m-5" />
              <LomavisH2
                title={t(
                  'platform_analytics.post_performance_title.contentPerformanceSection'
                )}
              />
              <div className="w-100 h-800px">
                <LomavisCard1
                  className="h-100 w-100"
                  title={
                    'platform_analytics.platform_content_type_barchart.title'
                  }
                  tooltipText={t(
                    'platform_analytics.platform_content_type_barchart.tooltipText'
                  )}
                >
                  <ILomavisPlatformContentType1
                    data={getPlatformContentMetrics(
                      postPerformanceData || [],
                      PLATFORM_LIST,
                      allContentTypePerformanceConfig,
                      allPlatformsPostPerformanceConfig
                    )}
                    zoomControl={false}
                  />
                </LomavisCard1>
              </div>
            </div>
          </div>
        )}
      </div>
      {loadingData ? (
        // 1. Loading state
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: '500px' }}
        >
          <Spinner animation="border" />
        </div>
      ) : postPerformanceData?.length === 0 ? (
        // 2. Empty state

        <div
          style={{
            height: `calc(100vh - ${navBarHeight}px)`
          }}
          className="w-100 d-flex  "
        >
          <div className="w-100 h-50 bg-white rounded p-2 d-flex flex-column gap-4 justify-content-center align-items-center">
            <i
              style={{
                fontSize: '3rem'
              }}
              className=" fa-duotone fa-regular fa-box-open "
            ></i>
            <span className="fs-2 w-50 text-center">
              <LomavisH3 title={t('platform_analytics.empty_state')} />
            </span>
          </div>
        </div>
      ) : (
        <div></div>
      )}
    </div>
  );
};

export default PostPerformance;
